import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import type { ApiError } from "@/lib/api"
import { apiRequest, getCsrfToken } from "@/lib/api"
import { getCable } from "@/lib/cable"
import type { Contact, Project } from "@/types"
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type {
  ColumnDef,
  PaginationState,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table"
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Download,
  Loader2,
  Pencil,
  Plus,
  Search,
  SlidersHorizontal,
  Trash2,
  Upload,
  Users,
  X,
} from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useParams } from "react-router-dom"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ContactsResponse = {
  contacts: Contact[]
  next_cursor: number | null
  prev_cursor: number | null
  total_count: number
}

type ContactsPageProps = {
  currentProject: Project | null
}

type ContactPayload = {
  facebook_psid?: string
  email?: string | null
  name?: string | null
  first_name?: string | null
  last_name?: string | null
  gender?: string | null
  is_subscribed?: boolean | null
  last_interaction_at?: string | null
  metadata?: Record<string, unknown> | null
}

type ImportState = {
  status: "idle" | "uploading" | "queued" | "running" | "completed" | "failed"
  processed: number
  imported: number
  errors: number
  errorSamples: { line: number | null; message: string }[]
  message?: string
}

const DEFAULT_IMPORT_STATE: ImportState = {
  status: "idle",
  processed: 0,
  imported: 0,
  errors: 0,
  errorSamples: [],
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const parseMetadata = (raw: string): Record<string, unknown> | null => {
  if (!raw.trim()) return {}
  try {
    return JSON.parse(raw) as Record<string, unknown>
  } catch {
    return null
  }
}

const normalizeDateTimeInput = (raw: string) => {
  const value = raw.trim()
  if (!value) return { value: null, error: null }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return { value: null, error: "A última interação deve ser uma data e hora válidas" }
  }
  return { value: parsed.toISOString(), error: null }
}

const toDateTimeLocalValue = (iso: string | null) => {
  if (!iso) return ""
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return ""
  const pad = (num: number) => String(num).padStart(2, "0")
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(
    parsed.getDate()
  )}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
}

const formatDate = (iso: string) => {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleDateString("pt-BR", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

const formatDateTime = (iso: string) => {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleString("pt-BR", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

const IMPORT_STATUS_LABELS: Record<ImportState["status"], string> = {
  idle: "ociosa",
  uploading: "enviando",
  queued: "na fila",
  running: "processando",
  completed: "concluída",
  failed: "falha",
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ContactsPage = ({ currentProject }: ContactsPageProps) => {
  const { projectSlug } = useParams<{ projectSlug: string }>()
  const queryClient = useQueryClient()

  // ---- Search / cursor-based pagination from API ----
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [cursor, setCursor] = useState<number | null>(null)
  const [direction, setDirection] = useState<"next" | "prev">("next")
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })

  // ---- Table state ----
  const [sorting, setSorting] = useState<SortingState>([])
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({})
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    name: false,
    gender: false,
    email: false,
    metadata: false,
  })

  // ---- Modals ----
  const [addOpen, setAddOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  // ---- Form state ----
  const [formPsid, setFormPsid] = useState("")
  const [formEmail, setFormEmail] = useState("")
  const [formName, setFormName] = useState("")
  const [formFirstName, setFormFirstName] = useState("")
  const [formLastName, setFormLastName] = useState("")
  const [formGender, setFormGender] = useState("")
  const [formIsSubscribed, setFormIsSubscribed] = useState(true)
  const [formLastInteractionAt, setFormLastInteractionAt] = useState("")
  const [formMetadata, setFormMetadata] = useState("")
  const [formError, setFormError] = useState<string | null>(null)
  const [editingContact, setEditingContact] = useState<Contact | null>(null)
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null)

  // ---- Import state ----
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importId, setImportId] = useState<string | null>(null)
  const [importState, setImportState] = useState<ImportState>(DEFAULT_IMPORT_STATE)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // ---- Debounce server search ----
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim())
      setCursor(null)
      setDirection("next")
      setPagination((prev) => ({ ...prev, pageIndex: 0 }))
    }, 300)
    return () => window.clearTimeout(timer)
  }, [search])

  // ---- ActionCable for imports ----
  useEffect(() => {
    if (!importId) return

    const cable = getCable()
    const subscription = cable.subscriptions.create(
      { channel: "ContactsImportsChannel", import_id: importId },
      {
        received: (payload: {
          event?: string
          processed?: number
          imported?: number
          errors?: number
          error_samples?: { line: number | null; message: string }[]
          error?: string
        }) => {
          if (payload.event === "contacts.import.failed") {
            setImportState((prev) => ({
              ...prev,
              status: "failed",
              message: payload.error || "Falha na importação",
            }))
            return
          }
          if (payload.event === "contacts.import.started") {
            setImportState((prev) => ({ ...prev, status: "running" }))
            return
          }
          if (payload.event === "contacts.import.progress") {
            setImportState((prev) => ({
              ...prev,
              status: "running",
              processed: payload.processed ?? prev.processed,
              imported: payload.imported ?? prev.imported,
              errors: payload.errors ?? prev.errors,
            }))
            return
          }
          if (payload.event === "contacts.import.completed") {
            setImportState((prev) => ({
              ...prev,
              status: "completed",
              processed: payload.processed ?? prev.processed,
              imported: payload.imported ?? prev.imported,
              errors: payload.errors ?? prev.errors,
              errorSamples: payload.error_samples ?? prev.errorSamples,
            }))
            queryClient.invalidateQueries({ queryKey: ["contacts", projectSlug] })
          }
        },
      }
    )
    return () => subscription.unsubscribe()
  }, [importId, projectSlug, queryClient])

  // ---- Fetch contacts ----
  const limit = pagination.pageSize
  const serverPage = pagination.pageIndex + 1

  const queryParams = useMemo(() => {
    const params = new URLSearchParams()
    if (debouncedSearch) params.set("q", debouncedSearch)
    if (cursor) params.set("cursor", String(cursor))
    params.set("limit", String(limit))
    params.set("direction", direction)
    return params.toString()
  }, [debouncedSearch, cursor, direction, limit])

  const { data, isLoading, isError, error, isFetching } = useQuery<
    ContactsResponse,
    ApiError
  >({
    queryKey: ["contacts", projectSlug, debouncedSearch, cursor, direction, limit],
    queryFn: () =>
      apiRequest<ContactsResponse>(
        `/api/projects/${encodeURIComponent(projectSlug || "")}/contacts?${queryParams}`
      ),
    enabled: Boolean(projectSlug),
    placeholderData: keepPreviousData,
  })

  const contacts = data?.contacts ?? []
  const nextCursor = data?.next_cursor ?? null
  const prevCursor = data?.prev_cursor ?? null
  const totalCount = data?.total_count ?? contacts.length
  const totalPages = Math.max(1, Math.ceil(totalCount / limit))

  // ---- Mutations ----
  const createMutation = useMutation<{ contact: Contact }, ApiError, ContactPayload>({
    mutationFn: (payload) =>
      apiRequest<{ contact: Contact }>(
        `/api/projects/${encodeURIComponent(projectSlug || "")}/contacts`,
        {
          method: "POST",
          body: JSON.stringify({ contact: payload }),
        }
      ),
    onSuccess: () => {
      resetForm()
      setAddOpen(false)
      setCursor(null)
      setDirection("next")
      setPagination((prev) => ({ ...prev, pageIndex: 0 }))
      queryClient.invalidateQueries({ queryKey: ["contacts", projectSlug] })
    },
  })

  const updateMutation = useMutation<
    { contact: Contact },
    ApiError,
    { id: number; payload: ContactPayload }
  >({
    mutationFn: ({ id, payload }) =>
      apiRequest<{ contact: Contact }>(
        `/api/projects/${encodeURIComponent(projectSlug || "")}/contacts/${id}`,
        {
          method: "PATCH",
          body: JSON.stringify({ contact: payload }),
        }
      ),
    onSuccess: () => {
      resetForm()
      setEditOpen(false)
      setEditingContact(null)
      queryClient.invalidateQueries({ queryKey: ["contacts", projectSlug] })
    },
  })

  const deleteMutation = useMutation<void, ApiError, { id: number }>({
    mutationFn: ({ id }) =>
      apiRequest(`/api/projects/${encodeURIComponent(projectSlug || "")}/contacts/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      setDeleteConfirmOpen(false)
      setContactToDelete(null)
      queryClient.invalidateQueries({ queryKey: ["contacts", projectSlug] })
    },
  })

  // ---- Handlers ----
  const resetForm = () => {
    setFormPsid("")
    setFormEmail("")
    setFormName("")
    setFormFirstName("")
    setFormLastName("")
    setFormGender("")
    setFormIsSubscribed(true)
    setFormLastInteractionAt("")
    setFormMetadata("")
    setFormError(null)
  }

  const handleCreate = () => {
    if (!projectSlug || !formPsid.trim()) return
    const parsed = parseMetadata(formMetadata)
    if (formMetadata.trim() && parsed === null) {
      setFormError("Os metadados devem estar em um JSON válido")
      return
    }
    const normalizedInteraction = normalizeDateTimeInput(formLastInteractionAt)
    if (normalizedInteraction.error) {
      setFormError(normalizedInteraction.error)
      return
    }
    setFormError(null)
    createMutation.mutate({
      facebook_psid: formPsid.trim(),
      email: formEmail.trim() || null,
      name: formName.trim() || null,
      first_name: formFirstName.trim() || null,
      last_name: formLastName.trim() || null,
      gender: formGender.trim() || null,
      is_subscribed: formIsSubscribed,
      last_interaction_at: normalizedInteraction.value,
      metadata: parsed,
    })
  }

  const openEdit = (contact: Contact) => {
    setEditingContact(contact)
    setFormEmail(contact.email ?? "")
    setFormName(contact.name ?? "")
    setFormFirstName(contact.first_name ?? "")
    setFormLastName(contact.last_name ?? "")
    setFormGender(contact.gender ?? "")
    setFormIsSubscribed(contact.is_subscribed ?? false)
    setFormLastInteractionAt(toDateTimeLocalValue(contact.last_interaction_at))
    setFormError(null)
    setEditOpen(true)
  }

  const handleUpdate = () => {
    if (!editingContact) return
    const normalizedInteraction = normalizeDateTimeInput(formLastInteractionAt)
    if (normalizedInteraction.error) {
      setFormError(normalizedInteraction.error)
      return
    }
    setFormError(null)
    updateMutation.mutate({
      id: editingContact.id,
      payload: {
        email: formEmail.trim() || null,
        name: formName.trim() || null,
        first_name: formFirstName.trim() || null,
        last_name: formLastName.trim() || null,
        gender: formGender.trim() || null,
        is_subscribed: formIsSubscribed,
        last_interaction_at: normalizedInteraction.value,
      },
    })
  }

  const openDeleteConfirm = (contact: Contact) => {
    setContactToDelete(contact)
    setDeleteConfirmOpen(true)
  }

  const handleDelete = () => {
    if (!contactToDelete) return
    deleteMutation.mutate({ id: contactToDelete.id })
  }

  const handleNext = () => {
    if (!nextCursor) return
    setDirection("next")
    setCursor(nextCursor)
    setPagination((prev) => ({
      ...prev,
      pageIndex: Math.min(prev.pageIndex + 1, Math.max(totalPages - 1, 0)),
    }))
  }

  const handlePrev = () => {
    if (!prevCursor) return
    setDirection("prev")
    setCursor(prevCursor)
    setPagination((prev) => ({
      ...prev,
      pageIndex: Math.max(prev.pageIndex - 1, 0),
    }))
  }

  const handleImport = async () => {
    if (!importFile || !projectSlug) return
    setUploadError(null)
    setImportState({ ...DEFAULT_IMPORT_STATE, status: "uploading" })
    try {
      const formData = new FormData()
      formData.append("file", importFile)
      const token = getCsrfToken()
      const response = await fetch(
        `/api/projects/${encodeURIComponent(projectSlug)}/contacts/import`,
        {
          method: "POST",
          credentials: "same-origin",
          headers: token ? { "X-CSRF-Token": token } : undefined,
          body: formData,
        }
      )
      const payload = await response.json()
      if (!response.ok) {
        const errorText =
          payload?.error ||
          (Array.isArray(payload?.errors) ? payload.errors.join(", ") : null) ||
          "Falha na importação"
        throw new Error(errorText)
      }
      setImportId(payload.import_id)
      setImportState((prev) => ({ ...prev, status: "queued" }))
      setImportFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ""
    } catch (err) {
      const message = err instanceof Error ? err.message : "Falha na importação"
      setUploadError(message)
      setImportState((prev) => ({ ...prev, status: "failed", message }))
    }
  }

  const handleExport = useCallback(() => {
    if (!contacts.length) return

    const headers = [
      "id",
      "facebook_psid",
      "email",
      "name",
      "first_name",
      "last_name",
      "gender",
      "is_subscribed",
      "last_interaction_at",
      "metadata",
      "created_at",
    ]
    const rows = contacts.map((c) =>
      [
        c.id,
        c.facebook_psid,
        c.email ?? "",
        c.name ?? "",
        c.first_name ?? "",
        c.last_name ?? "",
        c.gender ?? "",
        c.is_subscribed ?? "",
        c.last_interaction_at ?? "",
        JSON.stringify(c.metadata ?? {}).replace(/"/g, '""'),
        c.created_at,
      ]
        .map((v) => `"${v}"`)
        .join(",")
    )

    const csvContent = [headers.join(","), ...rows].join("\n")
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.setAttribute("download", `contatos-${projectSlug ?? "exportacao"}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, [contacts, projectSlug])

  // ---- Table columns ----
  const columns = useMemo<ColumnDef<Contact>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            indeterminate={table.getIsSomePageRowsSelected()}
            onChange={table.getToggleAllPageRowsSelectedHandler()}
            aria-label="Selecionar todos"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            aria-label="Selecionar linha"
          />
        ),
        enableSorting: false,
        enableHiding: false,
        enableGlobalFilter: false,
        size: 40,
      },
      {
        accessorKey: "facebook_psid",
        header: "PSID",
        cell: ({ row }) => {
          const displayName =
            row.original.name?.trim() ||
            [row.original.first_name, row.original.last_name].filter(Boolean).join(" ") ||
            row.original.facebook_psid ||
            "?"
          return (
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                {displayName.charAt(0).toUpperCase()}
              </div>
              <span className="font-medium truncate" title={row.original.facebook_psid ?? ""}>
                {row.original.facebook_psid ?? "—"}
              </span>
            </div>
          )
        },
        enableGlobalFilter: true,
      },
      {
        accessorKey: "name",
        header: "Nome",
        cell: ({ getValue }) => {
          const name = getValue() as string | null
          return name ? (
            <span className="font-medium truncate block" title={name}>{name}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )
        },
        enableGlobalFilter: true,
      },
      {
        accessorKey: "first_name",
        header: "Primeiro nome",
        cell: ({ getValue }) => {
          const value = getValue() as string | null
          return value ? (
            <span className="truncate block" title={value}>{value}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )
        },
        enableGlobalFilter: true,
      },
      {
        accessorKey: "last_name",
        header: "Sobrenome",
        cell: ({ getValue }) => {
          const value = getValue() as string | null
          return value ? (
            <span className="truncate block" title={value}>{value}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )
        },
        enableGlobalFilter: true,
      },
      {
        accessorKey: "gender",
        header: "Gênero",
        cell: ({ getValue }) => {
          const value = getValue() as string | null
          return value ? <span className="text-sm">{value}</span> : (
            <span className="text-muted-foreground">—</span>
          )
        },
        enableGlobalFilter: true,
      },
      {
        accessorKey: "is_subscribed",
        header: "Inscrito",
        cell: ({ getValue }) => {
          const value = getValue() as boolean | null
          if (value === null) return <span className="text-muted-foreground">—</span>
          return (
            <Badge variant={value ? "secondary" : "outline-solid"} className="text-[11px]">
              {value ? "Sim" : "Não"}
            </Badge>
          )
        },
        enableGlobalFilter: true,
      },
      {
        accessorKey: "last_interaction_at",
        header: "Última interação",
        cell: ({ getValue }) => {
          const value = getValue() as string | null
          return value ? (
            <span className="text-sm text-muted-foreground truncate block" title={formatDateTime(value)}>
              {formatDateTime(value)}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )
        },
        enableGlobalFilter: false,
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ getValue }) => {
          const email = getValue() as string | null
          return email ? (
            <span className="text-sm truncate block" title={email}>{email}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )
        },
        enableGlobalFilter: true,
      },
      {
        accessorKey: "metadata",
        header: "Metadados",
        cell: ({ getValue }) => {
          const metadata = getValue() as Record<string, unknown> | null
          if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
            return <span className="text-muted-foreground">—</span>
          }
          const keys = Object.keys(metadata)
          if (keys.length === 0) return <span className="text-muted-foreground">—</span>
          return (
            <div className="flex flex-wrap gap-1 overflow-hidden max-h-6">
              {keys.slice(0, 2).map((key) => (
                <Badge key={key} variant="secondary" className="text-[11px] truncate max-w-[120px]">
                  {key}: {String(metadata[key])}
                </Badge>
              ))}
              {keys.length > 2 && (
                <Badge variant="outline" className="text-[11px]">
                  +{keys.length - 2}
                </Badge>
              )}
            </div>
          )
        },
        enableSorting: false,
        enableGlobalFilter: false,
      },
      {
        accessorKey: "created_at",
        header: "Criado",
        cell: ({ getValue }) => {
          const value = getValue() as string | null
          return value ? (
            <span className="text-sm text-muted-foreground truncate block">
              {formatDate(value)}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )
        },
        enableGlobalFilter: false,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        enableHiding: false,
        enableGlobalFilter: false,
        size: 80,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => openEdit(row.original)}
            >
              <Pencil className="h-3.5 w-3.5" />
              <span className="sr-only">Editar</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={() => openDeleteConfirm(row.original)}
            >
              <Trash2 className="h-3.5 w-3.5" />
              <span className="sr-only">Excluir</span>
            </Button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  // ---- React Table instance ----
  const table = useReactTable({
    data: contacts,
    columns,
    state: { sorting, rowSelection, columnVisibility, pagination },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => String(row.id),
    manualPagination: true,
    rowCount: totalCount,
  })

  const selectedCount = Object.keys(rowSelection).length
  const isImporting = ["uploading", "queued", "running"].includes(importState.status)
  const isTableLoading = isLoading || isFetching
  const rowModel = table.getRowModel()

  const columnLabels: Record<string, string> = {
    facebook_psid: "PSID",
    name: "Nome",
    first_name: "Primeiro nome",
    last_name: "Sobrenome",
    gender: "Gênero",
    is_subscribed: "Inscrito",
    last_interaction_at: "Última interação",
    email: "Email",
    metadata: "Metadados",
    created_at: "Criado",
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Contatos do Facebook</h1>
          <p className="text-sm text-muted-foreground">
            Gerencie os contatos do Facebook para {currentProject?.name ?? "este projeto"}.
          </p>
        </div>
        <div className="flex items-center gap-2 mt-3 sm:mt-0">
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
            <Upload className="mr-1.5 h-3.5 w-3.5" />
            Importar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={contacts.length === 0}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Exportar
          </Button>
          <Button
            size="sm"
            onClick={() => {
              resetForm()
              setAddOpen(true)
            }}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Adicionar contato
          </Button>
        </div>
      </div>

      {/* Toolbar: search + page-size + selection info */}
      <div className="rounded-lg border bg-card text-card-foreground">
        <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar contatos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-3">
            {selectedCount > 0 && (
              <span className="text-sm text-muted-foreground">
                {selectedCount} {selectedCount === 1 ? "selecionado" : "selecionados"}
              </span>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8">
                  <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" />
                  Colunas
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Alternar colunas</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) => column.toggleVisibility(!!value)}
                    >
                      {columnLabels[column.id] ?? column.id}
                    </DropdownMenuCheckboxItem>
                  ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground whitespace-nowrap">Linhas</Label>
              <Select
                value={String(limit)}
                disabled={isTableLoading}
                onValueChange={(value) => {
                  const nextLimit = Number(value)
                  setPagination((prev) => ({
                    ...prev,
                    pageSize: nextLimit,
                    pageIndex: 0,
                  }))
                  setCursor(null)
                  setDirection("next")
                }}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 20, 30, 50].map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Import progress banner */}
        {importState.status !== "idle" && (
          <div className="border-t px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-sm">
                {isImporting && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                <span className="font-medium">{IMPORT_STATUS_LABELS[importState.status]}</span>
                <span className="text-muted-foreground">
                  — {importState.processed} processados · {importState.imported} importados · {importState.errors} erros
                </span>
              </div>
              {!isImporting && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => setImportState(DEFAULT_IMPORT_STATE)}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            {importState.message && (
              <p className="mt-1 text-sm text-destructive">{importState.message}</p>
            )}
            {importState.errorSamples.length > 0 && (
              <div className="mt-2 space-y-0.5">
                {importState.errorSamples.slice(0, 5).map((sample, i) => (
                  <p key={i} className="text-xs text-muted-foreground">
                    {sample.line ? `Linha ${sample.line}` : "Linha ?"}: {sample.message}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Table */}
        <div className="w-full max-w-full overflow-x-auto">
          <Table className="table-fixed min-w-full w-max">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const explicitSize = header.column.columnDef.size
                    return (
                    <TableHead
                      key={header.id}
                      style={explicitSize != null ? { width: explicitSize } : undefined}
                      className={header.column.getCanSort() ? "cursor-pointer select-none" : ""}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {header.isPlaceholder ? null : (
                        <div className="flex items-center gap-1">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() &&
                            ({
                              asc: <ArrowUp className="ml-1 h-3.5 w-3.5" />,
                              desc: <ArrowDown className="ml-1 h-3.5 w-3.5" />,
                            }[header.column.getIsSorted() as string] ?? (
                              <ChevronsUpDown className="ml-1 h-3 w-3 text-muted-foreground/50" />
                            ))}
                        </div>
                      )}
                    </TableHead>
                    )
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isTableLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-32 text-center">
                    <div className="flex items-center justify-center gap-2 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Carregando contatos...
                    </div>
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-32 text-center">
                    <p className="text-sm text-destructive">
                      {error?.message ?? "Falha ao carregar contatos."}
                    </p>
                  </TableCell>
                </TableRow>
              ) : rowModel.rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-32 text-center">
                    <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
                      <Users className="h-8 w-8" />
                      <p className="text-sm font-medium">Nenhum contato encontrado</p>
                      <p className="text-xs">
                        {search
                          ? "Tente ajustar sua busca."
                          : "Adicione seu primeiro contato para começar."}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                rowModel.rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="overflow-hidden">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between border-t px-4 py-3">
          <p className="text-sm text-muted-foreground">
            {contacts.length > 0
              ? `Mostrando ${rowModel.rows.length} de ${totalCount} contatos`
              : "Nenhum contato"}
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={handlePrev}
              disabled={!prevCursor || isTableLoading}
              title="Página anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="mx-2 text-sm text-muted-foreground tabular-nums">
              {serverPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={handleNext}
              disabled={!nextCursor || isTableLoading}
              title="Página seguinte"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* ============================================================= */}
      {/* Add Contact Dialog                                             */}
      {/* ============================================================= */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar contato</DialogTitle>
            <DialogDescription>
              Crie um novo contato para {currentProject?.name ?? "este projeto"}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="add-psid">PSID do Facebook *</Label>
              <Input
                id="add-psid"
                placeholder="ex.: 1234567890"
                value={formPsid}
                onChange={(e) => setFormPsid(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="add-first-name">Primeiro nome</Label>
                <Input
                  id="add-first-name"
                  placeholder="João"
                  value={formFirstName}
                  onChange={(e) => setFormFirstName(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="add-last-name">Sobrenome</Label>
                <Input
                  id="add-last-name"
                  placeholder="Silva"
                  value={formLastName}
                  onChange={(e) => setFormLastName(e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="add-name">Nome</Label>
                <Input
                  id="add-name"
                  placeholder="João Silva"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="add-email">Email</Label>
                <Input
                  id="add-email"
                  placeholder="joao@exemplo.com"
                  type="email"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="add-gender">Gênero</Label>
                <Input
                  id="add-gender"
                  placeholder="ex.: feminino"
                  value={formGender}
                  onChange={(e) => setFormGender(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="add-last-interaction">Última interação</Label>
                <Input
                  id="add-last-interaction"
                  type="datetime-local"
                  value={formLastInteractionAt}
                  onChange={(e) => setFormLastInteractionAt(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Checkbox
                id="add-subscribed"
                checked={formIsSubscribed}
                onChange={(e) => setFormIsSubscribed(e.target.checked)}
              />
              <Label htmlFor="add-subscribed">Inscrito</Label>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="add-metadata">Metadados (JSON)</Label>
              <Textarea
                id="add-metadata"
                placeholder='{"source": "web"}'
                value={formMetadata}
                onChange={(e) => setFormMetadata(e.target.value)}
                rows={3}
              />
            </div>
            {formError && <p className="text-sm text-destructive">{formError}</p>}
            {createMutation.isError && (
              <p className="text-sm text-destructive">{createMutation.error.message}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreate}
              disabled={createMutation.isPending || !formPsid.trim()}
            >
              {createMutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {createMutation.isPending ? "Criando..." : "Adicionar contato"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================================= */}
      {/* Edit Contact Dialog                                            */}
      {/* ============================================================= */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar contato</DialogTitle>
            <DialogDescription>
              Atualize os dados de{" "}
              <span className="font-medium text-foreground">
                {editingContact?.facebook_psid}
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="edit-first-name">Primeiro nome</Label>
                <Input
                  id="edit-first-name"
                  placeholder="João"
                  value={formFirstName}
                  onChange={(e) => setFormFirstName(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-last-name">Sobrenome</Label>
                <Input
                  id="edit-last-name"
                  placeholder="Silva"
                  value={formLastName}
                  onChange={(e) => setFormLastName(e.target.value)}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-name">Nome</Label>
              <Input
                id="edit-name"
                placeholder="João Silva"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                placeholder="joao@exemplo.com"
                type="email"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="edit-gender">Gênero</Label>
                <Input
                  id="edit-gender"
                  placeholder="ex.: feminino"
                  value={formGender}
                  onChange={(e) => setFormGender(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-last-interaction">Última interação</Label>
                <Input
                  id="edit-last-interaction"
                  type="datetime-local"
                  value={formLastInteractionAt}
                  onChange={(e) => setFormLastInteractionAt(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Checkbox
                id="edit-subscribed"
                checked={formIsSubscribed}
                onChange={(e) => setFormIsSubscribed(e.target.checked)}
              />
              <Label htmlFor="edit-subscribed">Inscrito</Label>
            </div>
            {formError && <p className="text-sm text-destructive">{formError}</p>}
            {updateMutation.isError && (
              <p className="text-sm text-destructive">{updateMutation.error.message}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending && (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              )}
              {updateMutation.isPending ? "Salvando..." : "Salvar alterações"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================================= */}
      {/* Delete Confirm Dialog                                          */}
      {/* ============================================================= */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Excluir contato</DialogTitle>
            <DialogDescription>
              Tem certeza de que deseja excluir{" "}
              <span className="font-medium text-foreground">
                {contactToDelete?.name ||
                  [contactToDelete?.first_name, contactToDelete?.last_name]
                    .filter(Boolean)
                    .join(" ") ||
                  contactToDelete?.facebook_psid}
              </span>
              ? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              )}
              {deleteMutation.isPending ? "Excluindo..." : "Excluir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================================= */}
      {/* Import Dialog                                                  */}
      {/* ============================================================= */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Importar contatos</DialogTitle>
            <DialogDescription>
              Envie um arquivo CSV com os cabeçalhos:{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">
                facebook_psid, email, name, first_name, last_name, gender, is_subscribed,
                last_interaction_at, metadata
              </code>
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="import-file">Arquivo CSV</Label>
              <Input
                id="import-file"
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
              />
            </div>
            {uploadError && <p className="text-sm text-destructive">{uploadError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                handleImport()
                setImportOpen(false)
              }}
              disabled={!importFile || isImporting}
            >
              {isImporting && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {isImporting ? "Importando..." : "Importar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ContactsPage
